import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Sparkles, ArrowRightLeft, Save, Info, Zap, Clock, Shield, RefreshCw,
  AlertTriangle, TrendingUp, Gauge,
} from 'lucide-react';

/**
 * FpRulesPage — full-page FP-economy control surface (iter 127).
 *
 * Single source of truth for every FP-related rule. Previously these
 * lived inside a compact tab on SettingsPage; splitting them out gives
 * room for every knob the house needs to tune the economy.
 */
export default function FpRulesPage() {
  const [rules, setRules] = useState({
    enabled: true,
    fp_to_usd_ratio: 5.0,
    min_convert: 10,
    max_convert: 0,
    auto_convert_enabled: false,
    auto_convert_threshold: 0,
    auto_redeem_on_threshold: false,
    max_fp_balance_cap: 0,
    fp_expiry_days: 0,
    conversion_cooldown_minutes: 0,
    first_time_intro_enabled: true,
  });
  const [streak, setStreak] = useState({
    enabled: true,
    schedule: [10, 20, 35, 50, 75, 100, 150],
    reminder_enabled: true,
    reminder_hour: 18,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [streakSaving, setStreakSaving] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rulesRes, streakRes] = await Promise.allSettled([
        api.get('/api/admin/fp-rules'),
        api.get('/api/admin/fp/streak'),
      ]);
      if (rulesRes.status === 'fulfilled') {
        const r = rulesRes.value.data?.rules || rulesRes.value.data || {};
        setRules(prev => ({ ...prev, ...r }));
      }
      if (streakRes.status === 'fulfilled' && streakRes.value.data) {
        const s = streakRes.value.data;
        setStreak({
          enabled: !!s.enabled,
          schedule: s.schedule || [10, 20, 35, 50, 75, 100, 150],
          reminder_enabled: s.reminder_enabled !== false,
          reminder_hour: typeof s.reminder_hour === 'number' ? s.reminder_hour : 18,
        });
      }
    } catch (e) {
      toast.error('Failed to load FP rules');
    } finally {
      setLoading(false);
    }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/fp-rules', {
        ...rules,
        fp_to_usd_ratio: Number(rules.fp_to_usd_ratio) || 5.0,
        min_convert: Number(rules.min_convert) || 0,
        max_convert: Number(rules.max_convert) || 0,
        auto_convert_threshold: Number(rules.auto_convert_threshold) || 0,
        max_fp_balance_cap: Number(rules.max_fp_balance_cap) || 0,
        fp_expiry_days: Number(rules.fp_expiry_days) || 0,
        conversion_cooldown_minutes: Number(rules.conversion_cooldown_minutes) || 0,
      });
      toast.success('FP rules saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveStreak = async () => {
    setStreakSaving(true);
    try {
      await api.put('/api/admin/fp/streak', {
        enabled: streak.enabled,
        schedule: streak.schedule.map(n => Number(n) || 0),
        reminder_enabled: streak.reminder_enabled,
        reminder_hour: Number(streak.reminder_hour) || 18,
      });
      toast.success('Streak schedule saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setStreakSaving(false);
    }
  };

  const setField = (k, v) => setRules(prev => ({ ...prev, [k]: v }));

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-400" data-testid="fp-rules-loading">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading FP rules…
      </div>
    );
  }

  const ratio = Number(rules.fp_to_usd_ratio) || 5;
  const effectivePct = Math.round((1 / ratio) * 100);
  const tenFpPreview = (10 / ratio).toFixed(2);

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="admin-fp-rules-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#F5D76E]" />
            Free Play Rules
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Complete configuration of the FP economy — conversion, auto-sweep, expiry, and daily streak drips.
          </p>
        </div>
        <Button
          onClick={saveRules}
          disabled={saving}
          className="bg-[#D4AF37] hover:bg-[#F5D76E] text-black font-bold"
          data-testid="save-fp-rules-main"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving…' : 'Save All'}
        </Button>
      </div>

      {/* ===== CONVERSION CORE ===== */}
      <Section
        icon={<ArrowRightLeft className="w-5 h-5 text-[#F5D76E]" />}
        title="Conversion Core"
        subtitle="Manual FP → $ swap rules that players initiate from their Wallet / Home tab."
        testid="section-conversion-core"
      >
        <Toggle
          label="Enable FP → $ Conversion"
          description="Master switch. When off, the FP converter button is hidden site-wide."
          value={rules.enabled}
          onChange={v => setField('enabled', v)}
          testid="toggle-fp-enabled"
        />
        <Row>
          <Field
            label="FP per $1 (ratio)"
            hint={`Currently: ${rules.fp_to_usd_ratio} FP = $1 → ${effectivePct}% of FP converts to main balance. Example: 10 FP = $${tenFpPreview}`}
            value={rules.fp_to_usd_ratio}
            onChange={v => setField('fp_to_usd_ratio', v)}
            testid="fp-ratio-input"
            type="number"
            step="0.01"
          />
          <Field
            label="Min Convert (FP)"
            hint="Player must request at least this much to convert."
            value={rules.min_convert}
            onChange={v => setField('min_convert', v)}
            testid="fp-min-input"
            type="number"
          />
          <Field
            label="Max per Conversion (FP, 0 = no cap)"
            hint="Caps each swap — e.g. 500 means bigger balances take multiple swaps."
            value={rules.max_convert}
            onChange={v => setField('max_convert', v)}
            testid="fp-max-input"
            type="number"
          />
        </Row>
      </Section>

      {/* ===== AUTO-SWEEP ===== */}
      <Section
        icon={<Zap className="w-5 h-5 text-purple-400" />}
        title="Auto-Sweep (Threshold Conversion)"
        subtitle="Automatically convert FP → $ when the balance grows large, with optional auto-redeem of FP-funded game accounts."
        testid="section-auto-sweep"
      >
        <Toggle
          label="Enable auto-convert when FP threshold hit"
          description="When a player's FP balance crosses the threshold below, the platform auto-converts everything to $ for them."
          value={rules.auto_convert_enabled}
          onChange={v => setField('auto_convert_enabled', v)}
          testid="toggle-auto-convert"
        />
        <Row>
          <Field
            label="Auto-convert threshold (FP)"
            hint="When balance ≥ this, sweep fires. Set to 100 to match the user's playthrough rule."
            value={rules.auto_convert_threshold}
            onChange={v => setField('auto_convert_threshold', v)}
            testid="fp-auto-threshold-input"
            type="number"
            disabled={!rules.auto_convert_enabled}
          />
          <Field
            label="Max FP wallet cap (0 = off)"
            hint="Soft ceiling — above this, ALL FP is swept regardless of threshold."
            value={rules.max_fp_balance_cap}
            onChange={v => setField('max_fp_balance_cap', v)}
            testid="fp-max-cap-input"
            type="number"
          />
        </Row>
        <Toggle
          label="Also auto-redeem FP-funded game accounts"
          description="When auto-sweep fires, games loaded with FP are redeemed first (regular redeem fee % + multiplier caps apply) so in-game winnings flow home before conversion."
          value={rules.auto_redeem_on_threshold}
          onChange={v => setField('auto_redeem_on_threshold', v)}
          testid="toggle-auto-redeem"
          disabled={!rules.auto_convert_enabled}
        />
      </Section>

      {/* ===== EXPIRY / COOLDOWN ===== */}
      <Section
        icon={<Clock className="w-5 h-5 text-amber-400" />}
        title="Lifecycle & Anti-Abuse"
        subtitle="Expiry windows and cooldowns protect the economy from farming + keep the FP bucket fresh."
        testid="section-lifecycle"
      >
        <Row>
          <Field
            label="FP expiry (days, 0 = never)"
            hint="Unused FP older than this is swept automatically. Protects against dormant stockpiles."
            value={rules.fp_expiry_days}
            onChange={v => setField('fp_expiry_days', v)}
            testid="fp-expiry-input"
            type="number"
          />
          <Field
            label="Manual convert cooldown (minutes, 0 = none)"
            hint="Minimum minutes between two manual conversions per player. Blocks spam-convert patterns."
            value={rules.conversion_cooldown_minutes}
            onChange={v => setField('conversion_cooldown_minutes', v)}
            testid="fp-cooldown-input"
            type="number"
          />
        </Row>
      </Section>

      {/* ===== PLAYER UX ===== */}
      <Section
        icon={<Shield className="w-5 h-5 text-blue-400" />}
        title="Player Experience"
        subtitle="Controls over the first-time explainer modal & surface behaviour."
        testid="section-player-ux"
      >
        <Toggle
          label="Show first-time FP explainer modal"
          description="New players see a one-time modal explaining Main $ vs FP on their first login. Recommended ON."
          value={rules.first_time_intro_enabled}
          onChange={v => setField('first_time_intro_enabled', v)}
          testid="toggle-first-time-intro"
        />
      </Section>

      {/* ===== DAILY STREAK ===== */}
      <Section
        icon={<TrendingUp className="w-5 h-5 text-green-400" />}
        title="Daily FP Streak Drip"
        subtitle="7-day consecutive-login ladder. Missing a day resets to Day 1. Feeds the FP economy & boosts DAU."
        testid="section-streak"
      >
        <Toggle
          label="Enable daily streak"
          value={streak.enabled}
          onChange={v => setStreak(p => ({ ...p, enabled: v }))}
          testid="toggle-streak-enabled"
        />
        <div className="grid grid-cols-7 gap-2" data-testid="streak-schedule-grid">
          {streak.schedule.map((amt, i) => (
            <div key={i} className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-wide text-gray-500 text-center">Day {i + 1}</label>
              <Input
                type="number"
                min={0}
                step="any"
                value={amt}
                onChange={e => setStreak(p => {
                  const next = [...p.schedule];
                  next[i] = parseFloat(e.target.value) || 0;
                  return { ...p, schedule: next };
                })}
                className="bg-[#0B0E14] border-white/10 text-center"
                data-testid={`streak-day-${i + 1}-input`}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-300">Streak reminder push</label>
            <button
              onClick={() => setStreak(p => ({ ...p, reminder_enabled: !p.reminder_enabled }))}
              className={`w-12 h-6 rounded-full transition-all ${streak.reminder_enabled ? 'bg-green-500' : 'bg-gray-600'}`}
              data-testid="toggle-streak-reminder"
              aria-label="Toggle streak reminder"
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-all ${streak.reminder_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
            <label className="text-xs text-gray-500">Fire at UTC hour</label>
            <Input
              type="number"
              min={0}
              max={23}
              value={streak.reminder_hour}
              onChange={e => setStreak(p => ({ ...p, reminder_hour: parseInt(e.target.value || '0', 10) }))}
              className="w-20 bg-[#0B0E14] border-white/10 text-center"
              data-testid="streak-reminder-hour"
            />
          </div>
          <Button
            onClick={saveStreak}
            disabled={streakSaving}
            className="bg-indigo-500 hover:bg-indigo-600 text-white"
            data-testid="save-streak-btn"
          >
            <Save className="w-4 h-4 mr-2" /> Save Streak
          </Button>
        </div>
      </Section>

      {/* ===== HEADS-UP FOOTER ===== */}
      <div
        className="rounded-xl p-4 flex gap-3 items-start"
        style={{
          background: 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(99,102,241,0.06))',
          border: '1px solid rgba(212,175,55,0.2)',
        }}
        data-testid="fp-rules-footer-tip"
      >
        <Info className="w-5 h-5 text-[#F5D76E] shrink-0 mt-0.5" />
        <div className="text-sm text-gray-300 leading-relaxed">
          <b className="text-white">Current rule:</b> Set the ratio to 5.0 for the house standard:
          <b className="text-[#F5D76E]"> 10 FP = $2</b>. That means <b className="text-[#F5D76E]">20%</b> of FP
          converts into main wallet balance.
        </div>
      </div>
    </div>
  );
}

// ----- Small building blocks (kept inline so the page stays self-contained) -----

function Section({ icon, title, subtitle, testid, children }) {
  return (
    <div
      className="rounded-2xl p-5 space-y-4 border border-white/5"
      style={{ background: 'rgba(255,255,255,0.02)' }}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{icon}</div>
        <div>
          <h2 className="font-bold text-white text-base">{title}</h2>
          <p className="text-xs text-gray-400 mt-0.5 max-w-3xl leading-relaxed">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
}

function Field({ label, hint, value, onChange, testid, type = 'number', step, disabled }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-gray-300">{label}</label>
      <Input
        type={type}
        step={step}
        value={value}
        disabled={disabled}
        onChange={e => onChange(type === 'number' ? parseFloat(e.target.value) : e.target.value)}
        className="bg-[#0B0E14] border-white/10"
        data-testid={testid}
      />
      {hint && <p className="text-[10px] text-gray-500 leading-snug">{hint}</p>}
    </div>
  );
}

function Toggle({ label, description, value, onChange, testid, disabled }) {
  return (
    <div className={`flex items-start justify-between gap-3 ${disabled ? 'opacity-50' : ''}`}>
      <div>
        <p className="text-sm font-semibold text-white">{label}</p>
        {description && <p className="text-xs text-gray-400 mt-0.5 max-w-2xl leading-snug">{description}</p>}
      </div>
      <button
        disabled={disabled}
        onClick={() => !disabled && onChange(!value)}
        className={`w-12 h-6 rounded-full transition-all shrink-0 ${value ? 'bg-green-500' : 'bg-gray-600'}`}
        data-testid={testid}
        aria-label={label}
      >
        <div className={`w-5 h-5 rounded-full bg-white transition-all ${value ? 'translate-x-6' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}
